import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SOVCategory, SOVCategoryFormGroup } from '@module-definition/category-group/common';
import { TranslateService } from '@ngx-translate/core';
import { CipoFieldTypes, CipoTextControl } from '@shared/components/fields/common/fields.model';
import { SaveButtonActions, SaveButtonType } from '@shared/components/save-button/common';
import { SaveButtonComponent } from '@shared/components/save-button/save-button.component';
import { CipoDialogModule } from '@shared/modules/cipo-dialog.module';
import { CipoFieldsModule } from '@shared/modules/cipo-fields.module';
import { CipoFormModule } from '@shared/modules/forms.module';
import { NotificationService } from '@shared/services';

@Component({
  selector: 'cipo-add-edit-category',
  imports: [CipoDialogModule, CipoFormModule, CipoFieldsModule, SaveButtonComponent],
  templateUrl: './add-edit-category.component.html',
})
export class AddEditSOVCategoryComponent {
  dialogData = inject<{
    category: SOVCategory;
    addCategory: (category: SOVCategory) => void;
    existingCategories: SOVCategory[];
  }>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<AddEditSOVCategoryComponent>);
  fb = inject(FormBuilder);
  notification = inject(NotificationService);
  translate = inject(TranslateService);

  category = signal(this.dialogData.category);
  SAVE_BUTTON_TYPE = SaveButtonType;
  form: FormGroup<SOVCategoryFormGroup>;

  constructor() {
    const number = new CipoTextControl(this.category().number ?? '', {
      type: CipoFieldTypes.Text,
      label: 'categoryGroup.numberColumn',
      required: true,
    });

    const name = new CipoTextControl(this.category().name ?? '', {
      type: CipoFieldTypes.Text,
      label: 'categoryGroup.nameColumn',
      required: true,
    });

    this.form = this.fb.group({
      number,
      name,
    });
  }

  save(type: SaveButtonActions) {
    const { number, name } = this.form.getRawValue();

    if (!this.checkExists(number as string, name as string)) {
      const category: SOVCategory = {
        id: this.category().id,
        rootCategoryId: this.category().rootCategoryId,
        number: number as string,
        name: name as string,
        position: this.category().position ?? 0,
        uniqueKey: this.category().uniqueKey ?? crypto.randomUUID(),
        modified: true,
        visible: this.category().visible ?? true,
        isUsed: this.category().isUsed ?? false,
      };

      if (type === SaveButtonActions.SaveAndAddNew) {
        // Save category and keep dialog open for new category
        this.dialogData.addCategory(category);
        // Reset dialog data for new category
        this.category.set({
          id: 0,
          rootCategoryId: 0,
          name: '',
          number: '',
          position: 0,
          uniqueKey: crypto.randomUUID(),
          modified: true,
          visible: true,
          isUsed: false,
        });
        this.form.setValue({ name: '', number: '' });
        this.form.markAsUntouched();
        this.form.updateValueAndValidity();

        // In case we are on multiple addings, then we need to add new categories so when we try to add them again, to throw and error
        if (category.id) {
          // Remove the existing one so we can add it again
          this.dialogData.existingCategories = this.dialogData.existingCategories.filter(
            c => c.uniqueKey === category.uniqueKey,
          );
        }
        // Add new category so next time to check with this one
        this.dialogData.existingCategories.push(category);
      } else if (type === SaveButtonActions.Save) {
        this.dialogRef.close(category);
      }
    }
  }

  checkExists(number: string, name: string) {
    const existingOnNumberAndName = this.dialogData.existingCategories.some(
      e =>
        e.uniqueKey != this.category().uniqueKey &&
        number.toLowerCase() === e.number.toLowerCase() &&
        e.name.toLowerCase() === name.toLowerCase(),
    );

    if (existingOnNumberAndName) {
      this.notification.error(`${number} - ${name} ${this.translate.instant('categoryGroup.alreadyExists')}`);
    }

    const existingOnNumber = this.dialogData.existingCategories.some(
      e => e.uniqueKey != this.category().uniqueKey && number.toLowerCase() === e.number.toLowerCase(),
    );

    if (existingOnNumber) {
      this.notification.error(
        `${this.translate.instant('categoryGroup.numberColumn')}: ${number} ${this.translate.instant('categoryGroup.alreadyExists')}`,
      );
    }

    return existingOnNumberAndName || existingOnNumber;
  }
}
